import { useEffect, useState, useRef, useCallback } from "react";
import logo from "../../../assets/Logo.png";
import googleLogo from "../../../assets/googleIcon.svg";
import LockIcon from "../../../assets/LockIcon";
import EmailIcon from "../../../assets/EmailIcon";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import { loginUserAsync, loginWithGoogleAsync } from "../../../store/thunks/OrganizationThunk";
import { STATUS } from "../../../store/slices/OrganizationSlice";
import { formatSecondsToTimer } from "../../../lib/utils";

declare global {
  interface Window {
    google: any;
  }
}

const Login = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [retrySecondsLeft, setRetrySecondsLeft] = useState<number | null>(null);
    const dispatch = useDispatch<AppDispatch>();
    const loginDetails = useSelector(
        (state: RootState) => state.OrganizationListStore
    );
    const googleButtonRef = useRef<HTMLDivElement>(null);
    const isLoginDisabled = loading || !email.trim() || !password.trim() || (retrySecondsLeft !== null && retrySecondsLeft > 0);

    const handleLogin = async () => {
        setLoading(true);
        setError('');
        await dispatch(loginUserAsync({ email, password }));
        setLoading(false);
    };

    const handleGoogleSignIn = useCallback(async (credentialResponse: any) => {
        setGoogleLoading(true);
        setError('');
        try {
            await dispatch(loginWithGoogleAsync(credentialResponse.credential));
        } catch (err) {
            setGoogleLoading(false);
        }
    }, [dispatch]);

    // Initialize Google OAuth
    useEffect(() => {
        const initializeGoogleSignIn = () => {
            if (window.google && googleButtonRef.current) {
                window.google.accounts.id.initialize({
                    client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                    callback: handleGoogleSignIn,
                });

                window.google.accounts.id.renderButton(
                    googleButtonRef.current,
                    {
                        theme: 'outline',
                        size: 'large',
                        text: 'continue_with',
                        width: 343,
                    }
                );
            }
        };

        // Wait for Google script to load
        if (window.google) {
            initializeGoogleSignIn();
        } else {
            const checkGoogle = setInterval(() => {
                if (window.google) {
                    initializeGoogleSignIn();
                    clearInterval(checkGoogle);
                }
            }, 100);

            // Cleanup interval after 10 seconds
            setTimeout(() => clearInterval(checkGoogle), 10000);
        }

        // Cleanup function
        return () => {
            // Google button cleanup is handled by Google's library
        };
    }, [handleGoogleSignIn]);

    useEffect(() => {
        const updateRetryTimer = () => {
            if (loginDetails.loginRetryUntil) {
                const remainingSeconds = Math.max(
                    0,
                    Math.ceil((loginDetails.loginRetryUntil - Date.now()) / 1000)
                );
                setRetrySecondsLeft(remainingSeconds > 0 ? remainingSeconds : null);
            } else {
                setRetrySecondsLeft(null);
            }
        };

        updateRetryTimer();
        const interval = loginDetails.loginRetryUntil
            ? window.setInterval(updateRetryTimer, 1000)
            : undefined;

        return () => {
            if (interval) {
                window.clearInterval(interval);
            }
        };
    }, [loginDetails.loginRetryUntil]);

    useEffect(() => {
        if (loginDetails.loginStatus === STATUS.LOADING) {
            setError("");
            setGoogleLoading(false);
            return;
        }

        if (loginDetails.loginStatus === STATUS.SUCCESS) {
            const accessToken: string | undefined = loginDetails?.loginResponse?.access_token;
            localStorage.setItem('accessToken', accessToken as string);
            navigate(`/org-view`);
        } else if (loginDetails.loginStatus === STATUS.FAILED) {
            if (!retrySecondsLeft && loginDetails.loginRetryUntil && loginDetails.loginRetryUntil <= Date.now()) {
                setError('');
                return;
            }

            const baseError = loginDetails.error || "Login failed. Please check your credentials.";
            const timerText = retrySecondsLeft && retrySecondsLeft > 0
                ? formatSecondsToTimer(retrySecondsLeft)
                : null;

            const retryText = timerText
                ? baseError.replace(/\b(after|in)\s+\d+\s*minutes?\b/i, `in ${timerText}`)
                    .replace(/\bafter\s+\d+\s*minutes?\b/i, `in ${timerText}`)
                    .replace(/\bin\s+\d+\s*minutes?\b/i, `in ${timerText}`)
                : baseError;

            const finalText = timerText && !/in\s+\d+:\d+/.test(retryText)
                ? `${retryText} Try again in ${timerText}`
                : retryText;

            setError(finalText.trim());
            setGoogleLoading(false);
        }
    }, [loginDetails.loginStatus, loginDetails.ParentOrganizationList, navigate, loginDetails.error, retrySecondsLeft]);

    return (
        <div className="flex flex-col px-12 justify-center items-center  h-screen">
            <img src={logo} height="40px" width="100px" alt="Logo" />
            <div className="mt-24 relative">
                <input
                    type="text"
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    value={email}
                    className="w-[343px] border-[1.5px] px-3 h-[56px] pl-14 border-black rounded-[20px]"
                    placeholder="abc@example.com"
                />
                <EmailIcon />
            </div>
            <div className="mt-4">
                <div className="relative">
                    <input
                        type="password"
                        onChange={(e) => { setPassword(e.target.value); setError(''); }}
                        value={password}
                        className=" w-[343px] border-[1.5px] pl-14 px-3 h-[54px] border-black rounded-2xl"
                        placeholder="*********"
                    />
                    <LockIcon />
                </div>
            </div>
            {error && <div className="text-red-500 mt-2">{error}</div>}
            <div className="text cursor-pointer text-blue mt-4 underline">
                Forgot Password?
            </div>
            <button
                onClick={handleLogin}
                className={`cursor-pointer bg-loginBg mt-4 text2 text-white px-8 py-2 rounded-3xl ${isLoginDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={isLoginDisabled}
            >
                {loading ? 'Logging in...' : 'Login'} {/* Show loader text */}
            </button>
            <div className="mt-16 border-t-2 pt-[15px] w-full flex justify-center ">
                <div className="flex ">
                    <div className="text3">Don’t have an account? </div>
                    <div className="text2 cursor-pointer text-blue ml-1 underline">Register</div>
                </div>
            </div>
            <div
                ref={googleButtonRef}
                className="mt-[15px] flex justify-center"
                style={{ minHeight: '40px' }}
            />
        </div>
    );
}

export default Login;
